import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { downgradeInjectable } from '@angular/upgrade/static';
import { catchError, EMPTY } from 'rxjs';
import App from '@app/app';
import { WINDOW } from '@app/core/window.token';
import type { EventAction } from '@app/interface/action/event-action.interface';
import type { FlightRecorderService } from '@app/interface/common.interface';
import type { SessionStateResponse } from '@app/interface/session/session-state-response.interface';
import { FeatureFlagService } from '@app/service/feature-flag.service';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';

type HotjarWindow = Window & {
  hj?: (command: 'identify', userId: string | null, attributes: { instanceId: string }) => void;
};

/**
 * Maps the identity of the live session (SessionDataService.instanceId) to Hotjar, so a
 * support/log investigation can jump from a log line's instanceId straight to the matching Hotjar
 * session recording.
 *
 * Deliberately its own service, not folded into SessionSyncService: that service's fetch is gated
 * behind the SESSION_SYNC flag and skipped entirely when it is off (see InitService.init()) --
 * log/Hotjar correlation is a different, always-relevant concern and must not go dark just
 * because SESSION_SYNC is disabled or later removed. When both flags are enabled at bootstrap,
 * InitService passes SessionSyncService's already-fetched state here rather than issuing a second
 * request.
 *
 * Called fire-and-forget from InitService.init() -- not part of the blocking bootstrap promises.
 * Losing this call costs an occasionally-uncorrelated Hotjar recording, not app functionality, so
 * it must not hold up the splash screen or fail the app on a network error.
 */
@Injectable({
  providedIn: 'root'
})
export class HotjarSessionMappingService {
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly flightRecorderService: FlightRecorderService = inject(angularjs.FlightRecorderService);
  private readonly injector = inject(Injector);
  private readonly sessionRecreatedAction: EventAction = inject(angularjs.SessionRecreatedAction);
  private readonly sessionTimeoutAction: EventAction = inject(angularjs.SessionTimeoutAction);
  private readonly window = inject(WINDOW) as HotjarWindow;
  private hotjarInstanceId: string | null = null;
  private hotjarUserId: string | null = null;

  constructor() {
    this.sessionTimeoutAction.register(() => this.resetSessionMapping());
    this.sessionRecreatedAction.register(() => this.mapSession());
  }

  // Lazily resolved, like AccountApi.http -- HttpClient depends on interceptors that may not be
  // ready at this service's own construction time.
  private get http(): HttpClient {
    return this.injector.get(HttpClient);
  }

  /**
   * Fetches the current session's instanceId and identifies it in Hotjar.
   *
   * Subscribes internally rather than returning the Observable for the caller to chain: this is
   * intentionally fire-and-forget from InitService.init(), and an Observable no caller subscribes
   * to would never execute at all. No-ops silently on failure -- same reasoning as
   * SessionSyncService.fetchServerSessionState: a missed identify call is not worth surfacing as
   * an error, and nothing here can safely retry into a bootstrap that has already moved on.
   */
  mapSession(): void {
    if (!this.featureFlagService.isEnabled('HOTJAR_SESSION_MAPPING')) {
      return;
    }

    this.http
      .get<SessionStateResponse>('session')
      .pipe(
        catchError(() => {
          this.flightRecorderService.log('HotjarSessionMappingService:fetchFailed');

          return EMPTY;
        })
      )
      .subscribe((response) => this.identifySession(response?.data?.instanceId));
  }

  /** Maps a known instanceId without fetching the session endpoint again. */
  identifySession(instanceId: string | null | undefined): void {
    if (instanceId) {
      this.hotjarInstanceId = instanceId;
      this.pushIdentify();
    }
  }

  /**
   * Updates the user identity to include with the current session's Hotjar mapping. A falsy value
   * clears the identity used for future calls, such as after logout.
   */
  identifyUser(lifetouchUserProfileId: string | null | undefined): void {
    this.hotjarUserId = lifetouchUserProfileId || null;
    this.pushIdentify();
  }

  private resetSessionMapping(): void {
    this.hotjarInstanceId = null;
    this.hotjarUserId = null;
  }

  private pushIdentify(): void {
    try {
      if (!this.featureFlagService.isEnabled('HOTJAR_SESSION_MAPPING') || !this.window.hj || !this.hotjarInstanceId) {
        return;
      }

      this.window.hj('identify', this.hotjarUserId, { instanceId: this.hotjarInstanceId });
    } catch {
      // Hotjar is optional and loaded asynchronously by GTM.
    }
  }
}

App.factory('HotjarSessionMappingService', downgradeInjectable(HotjarSessionMappingService));
